import React, { useState, useRef } from 'react';
import { Upload, X, Eye, Download, AlertCircle } from 'lucide-react';
import { http, BACKEND_URL } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';

const ImagenesPresupuesto = ({ presupuestoId, imagenes, onImagenesChange, puedeEditar }) => {
  const [uploading, setUploading] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);
  const { toast } = useToast();

  const handleFileSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) return;

    if (!presupuestoId) {
      toast({
        title: "Error",
        description: "Debe guardar el presupuesto antes de subir imágenes",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);

    try {
      const nuevasImagenes = [];
      
      for (const file of files) {
        // Validaciones
        if (!file.type.startsWith('image/')) {
          toast({
            title: "Archivo inválido",
            description: `${file.name} no es una imagen válida`,
            variant: "destructive",
          });
          continue;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB
          toast({
            title: "Archivo muy grande",
            description: `${file.name} es mayor a 5MB`,
            variant: "destructive",
          });
          continue;
        }

        // Subir imagen
        const formData = new FormData();
        formData.append('file', file);

        try {
          const response = await http.post(`/presupuestos/${presupuestoId}/imagenes`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          nuevasImagenes.push(response.data);
        } catch (error) {
          console.error('Error subiendo imagen:', error);
          toast({
            title: "Error al subir imagen",
            description: error.response?.data?.detail || `Error al subir ${file.name}`,
            variant: "destructive",
          });
        }
      }

      if (nuevasImagenes.length > 0) {
        const imagenesActualizadas = [...imagenes, ...nuevasImagenes];
        onImagenesChange(imagenesActualizadas);
        
        toast({
          title: "Imágenes subidas",
          description: `${nuevasImagenes.length} imagen(es) subida(s) correctamente`,
        });
      }

    } catch (error) {
      console.error('Error general:', error);
      toast({
        title: "Error",
        description: "Error al procesar las imágenes",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
      // Limpiar input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleEliminarImagen = async (imagen) => {
    try {
      await http.delete(`/presupuestos/${presupuestoId}/imagenes/${imagen.id}`);
      
      const imagenesActualizadas = imagenes.filter(img => img.id !== imagen.id);
      onImagenesChange(imagenesActualizadas);
      
      toast({
        title: "Imagen eliminada",
        description: "La imagen se eliminó correctamente",
      });
    } catch (error) {
      console.error('Error eliminando imagen:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Error al eliminar la imagen",
        variant: "destructive",
      });
    }
  };

  const abrirVistaPrevia = (imagen) => {
    setPreviewImage(imagen);
  };

  const descargarImagen = (imagen) => {
    const link = document.createElement('a');
    link.href = `${BACKEND_URL}${imagen.url}`;
    link.download = imagen.filename;
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          Imágenes adjuntas
        </label>
        
        {puedeEditar && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || !presupuestoId}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Upload className="w-4 h-4 mr-2" />
            {uploading ? 'Subiendo...' : 'Subir imágenes'}
          </button>
        )}
      </div>

      {!presupuestoId && puedeEditar && (
        <div className="flex items-center p-3 bg-yellow-50 rounded-lg border border-yellow-200">
          <AlertCircle className="w-5 h-5 text-yellow-600 mr-2" />
          <span className="text-sm text-yellow-800">
            Guarde el presupuesto antes de subir imágenes
          </span>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      {/* Grid de imágenes */}
      {imagenes && imagenes.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {imagenes.map((imagen) => (
            <div key={imagen.id} className="relative group">
              <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden border">
                <img
                  src={`${BACKEND_URL}${imagen.url}`}
                  alt={imagen.filename}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="50" font-family="Arial" font-size="12" fill="%23666" text-anchor="middle" dy="0.3em">Imagen no disponible</text></svg>';
                  }}
                />
              </div>
              
              {/* Overlay con acciones */}
              <div className="absolute inset-0 bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity duration-200 rounded-lg flex items-center justify-center">
                <div className="flex space-x-2">
                  <button
                    type="button"
                    onClick={() => abrirVistaPrevia(imagen)}
                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Ver imagen"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  
                  <button
                    type="button"
                    onClick={() => descargarImagen(imagen)}
                    className="p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors"
                    title="Descargar imagen"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  
                  {puedeEditar && (
                    <button
                      type="button"
                      onClick={() => handleEliminarImagen(imagen)}
                      className="p-2 bg-red-500 rounded-full text-white hover:bg-red-600 transition-colors"
                      title="Eliminar imagen"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              
              {/* Nombre del archivo */}
              <div className="mt-2">
                <p className="text-xs text-gray-600 truncate" title={imagen.filename}>
                  {imagen.filename}
                </p>
                <p className="text-xs text-gray-400">
                  {(imagen.size / 1024).toFixed(1)} KB
                </p>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
          <p className="text-sm text-gray-500">
            {puedeEditar ? 'No hay imágenes adjuntas. Haga clic en "Subir imágenes" para agregar.' : 'No hay imágenes adjuntas'}
          </p>
        </div>
      )}

      {/* Modal de vista previa */}
      {previewImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50" onClick={() => setPreviewImage(null)}>
          <div className="relative max-w-4xl max-h-[90vh] mx-4">
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-4 right-4 p-2 bg-white rounded-full text-gray-700 hover:bg-gray-100 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>
            
            <img
              src={`${BACKEND_URL}${previewImage.url}`}
              alt={previewImage.filename}
              className="max-w-full max-h-full object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            
            <div className="absolute bottom-4 left-4 bg-black bg-opacity-75 text-white px-3 py-2 rounded">
              <p className="text-sm font-medium">{previewImage.filename}</p>
              <p className="text-xs opacity-75">
                {(previewImage.size / 1024).toFixed(1)} KB
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagenesPresupuesto;